import { useState, useEffect, useCallback, useRef } from "react";
import {
  Routes,
  Route,
  Navigate,
  NavLink,
  Outlet,
  useNavigate,
  useParams,
  useLocation,
  useOutletContext,
} from "react-router-dom";

const API = "https://api.perfect.my/api/installer/v1";
const BLUE = "#0052cc";
const BLUE_HOV = "#2979e8";

// ─── helpers ────────────────────────────────────────────────────────────────
const fmt = (cents) => `RM ${(cents / 100).toFixed(2)}`;

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

function useApi(token, onUnauthorized) {
  const req = useCallback(
    async (method, path, body, multipart) => {
      const headers = { Authorization: `Bearer ${token}` };
      if (!multipart) headers["Content-Type"] = "application/json";
      const res = await fetch(`${API}${path}`, {
        method,
        headers,
        body: multipart ? body : body ? JSON.stringify(body) : undefined,
      });
      if (res.status === 401) { onUnauthorized?.(); return; }
      const json = await res.json().catch(() => ({}));
      if (!json.success && res.status !== 200 && res.status !== 201) {
        const err = new Error(json.message || `Error ${res.status}`);
        err.data = json;
        throw err;
      }
      return json;
    },
    [token, onUnauthorized]
  );
  return {
    get: (p) => req("GET", p),
    post: (p, b) => req("POST", p, b),
    patch: (p, b) => req("PATCH", p, b),
    postForm: (p, fd) => req("POST", p, fd, true),
  };
}

// ─── tiny UI kit ────────────────────────────────────────────────────────────
const Spinner = ({ size = 20 }) => (
  <span
    style={{
      display: "inline-block",
      width: size,
      height: size,
      border: `2px solid rgba(0,82,204,.2)`,
      borderTopColor: BLUE,
      borderRadius: "50%",
      animation: "spin .7s linear infinite",
    }}
  />
);

const Btn = ({ children, onClick, variant = "primary", disabled, small, style = {} }) => {
  const [hov, setHov] = useState(false);
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: small ? "6px 14px" : "9px 20px",
    fontSize: small ? 13 : 14,
    fontWeight: 600,
    borderRadius: 8,
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "background .15s, opacity .15s",
    opacity: disabled ? 0.55 : 1,
    ...style,
  };
  const styles =
    variant === "primary"
      ? { background: hov && !disabled ? BLUE_HOV : BLUE, color: "#fff" }
      : variant === "danger"
      ? { background: hov && !disabled ? "#c0392b" : "#e74c3c", color: "#fff" }
      : variant === "ghost"
      ? {
          background: hov && !disabled ? "#f0f4ff" : "transparent",
          color: BLUE,
          border: `1px solid ${BLUE}`,
        }
      : { background: hov && !disabled ? "#f1f1f1" : "#f8f8f8", color: "#333", border: "1px solid #ddd" };
  return (
    <button
      style={{ ...base, ...styles }}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      {children}
    </button>
  );
};

const Badge = ({ children, color = "blue" }) => {
  const map = {
    blue: { bg: "#e6f0ff", text: BLUE },
    green: { bg: "#e6f4ea", text: "#1a7f3c" },
    amber: { bg: "#fff8e1", text: "#b36b00" },
    red: { bg: "#fdecea", text: "#b71c1c" },
    gray: { bg: "#f0f0f0", text: "#555" },
  };
  const c = map[color] || map.gray;
  return (
    <span
      style={{
        background: c.bg,
        color: c.text,
        fontSize: 11,
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: 20,
        letterSpacing: 0.3,
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
};

const statusColor = (s) =>
  ({ pending: "amber", installing: "blue", completed: "green", failed: "red", cancelled: "red" }[s] || "gray");

const CANCEL_REASONS = [
  { id: "competitor", label: "Order countered by competitor" },
  { id: "not_interested", label: "Stops being interested" },
  { id: "need_more_time", label: "Need more time" },
  { id: "other", label: "Others" },
];

const CONTACT_BACK_OPTIONS = [
  { id: "6_months", label: "6 months" },
  { id: "1_year", label: "1 year" },
  { id: "2_years", label: "2 years" },
];

const OTHERS_COMPETITOR_ID = "__others__";

const reasonLabel = (id) => CANCEL_REASONS.find((r) => r.id === id)?.label || id;
const contactBackLabel = (id) => CONTACT_BACK_OPTIONS.find((c) => c.id === id)?.label || id;

const Card = ({ children, style = {}, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: "#fff",
      borderRadius: 12,
      border: "1px solid #e8ecf0",
      padding: "20px 24px",
      ...style,
    }}
  >
    {children}
  </div>
);

const Label = ({ children }) => (
  <span style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>
    {children}
  </span>
);

const Toast = ({ msg, type, onClose }) => {
  const isMobile = useIsMobile();
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      style={{
        position: "fixed",
        bottom: isMobile ? 80 : 24,
        right: isMobile ? 12 : 24,
        left: isMobile ? 12 : "auto",
        background: type === "error" ? "#c0392b" : "#1a7f3c",
        color: "#fff",
        padding: "12px 20px",
        borderRadius: 10,
        fontSize: 14,
        fontWeight: 500,
        maxWidth: isMobile ? "100%" : 340,
        zIndex: 9999,
        boxShadow: "0 4px 20px rgba(0,0,0,.2)",
      }}
    >
      {msg}
      <button
        onClick={onClose}
        style={{ marginLeft: 12, background: "none", border: "none", color: "#fff", cursor: "pointer", fontWeight: 700 }}
      >
        ×
      </button>
    </div>
  );
};

// ─── Login ──────────────────────────────────────────────────────────────────
function Login({ onLogin }) {
  const isMobile = useIsMobile();
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: pass }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Login failed");
      onLogin(json.token, json.data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg,#e8f0ff 0%,#f5f8ff 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? "20px 16px" : 0,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: isMobile ? 16 : 16,
          padding: isMobile ? "32px 20px" : "40px 36px",
          width: "100%",
          maxWidth: 400,
          boxShadow: "0 8px 40px rgba(0,82,204,.12)",
        }}
      >
        <div style={{ marginBottom: 28 }}>
          <img src="/logo.svg" alt="Perfect Tech" style={{ height: 36, display: "block", marginBottom: 6 }} />
          <div style={{ fontSize: 12, fontWeight: 600, color: "#888", letterSpacing: 0.5, textTransform: "uppercase" }}>Installer Portal</div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <Label>Email</Label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="installer@example.com"
            style={inputStyle}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <Label>Password</Label>
          <input
            type="password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            placeholder="••••••••"
            style={inputStyle}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </div>
        {err && <div style={{ color: "#c0392b", fontSize: 13, marginBottom: 14 }}>{err}</div>}
        <Btn onClick={submit} disabled={loading || !email || !pass} style={{ width: "100%", justifyContent: "center" }}>
          {loading ? <Spinner size={16} /> : "Sign In"}
        </Btn>
      </div>
    </div>
  );
}

const inputStyle = {
  display: "block",
  width: "100%",
  marginTop: 6,
  padding: "9px 12px",
  border: "1px solid #d0d7e3",
  borderRadius: 8,
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};

// ─── Nav ────────────────────────────────────────────────────────────────────
const NAV = [
  { id: "available", path: "/available", label: "Available", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" },
  { id: "myjobs", path: "/myjobs", label: "My Jobs", icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" },
  { id: "addonjobs", path: "/addon-jobs", label: "Add-ons", icon: "M20 12a8 8 0 11-16 0 8 8 0 0116 0zM12 8v4l3 3" },
  { id: "wallet", path: "/wallet", label: "Wallet", icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" },
  { id: "withdrawals", path: "/withdrawals", label: "Withdraw", icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" },
  { id: "profile", path: "/profile", label: "Profile", icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" },
];

function useActiveNavId() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/jobs/")) return "myjobs";
  if (pathname.startsWith("/addon-jobs")) return "addonjobs";
  const match = NAV.find((n) => n.path === pathname);
  return match?.id || "available";
}

const ADDON_JOB_STATUS_COLOR = { pending: "amber", assigned: "blue", completed: "green", rejected: "red", cancelled: "gray" };
const ADDON_JOB_STATUS_LABEL = { pending: "Available", assigned: "Scheduled", completed: "Installed", rejected: "Declined", cancelled: "Cancelled" };

function Sidebar({ user, onLogout }) {
  const isMobile = useIsMobile();
  const activeTab = useActiveNavId();
  if (isMobile) return null;
  return (
    <div
      style={{
        width: 220,
        minHeight: "100vh",
        background: "#fff",
        borderRight: "1px solid #e8ecf0",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
      }}
    >
      <div style={{ padding: "18px 16px 14px", borderBottom: "1px solid #f0f0f0" }}>
        <img src="/logo.svg" alt="Perfect Tech" style={{ height: 28, display: "block", marginBottom: 4 }} />
        <div style={{ fontSize: 10, fontWeight: 600, color: "#888", letterSpacing: 0.5, textTransform: "uppercase" }}>Installer</div>
      </div>
      <nav style={{ flex: 1, padding: "12px 8px" }}>
        {NAV.map((n) => {
          const active = activeTab === n.id;
          return (
            <NavLink
              key={n.id}
              to={n.path}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                width: "100%",
                padding: "9px 12px",
                borderRadius: 8,
                border: "none",
                background: active ? "#e8f0ff" : "transparent",
                color: active ? BLUE : "#555",
                fontWeight: active ? 600 : 400,
                fontSize: 13.5,
                cursor: "pointer",
                marginBottom: 2,
                textAlign: "left",
                textDecoration: "none",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d={n.icon} />
              </svg>
              {n.label}
            </NavLink>
          );
        })}
      </nav>
      <div style={{ padding: "12px 16px", borderTop: "1px solid #f0f0f0" }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 2 }}>{user?.name}</div>
        <div style={{ fontSize: 11, color: "#888", marginBottom: 10 }}>{user?.email}</div>
        <Btn onClick={onLogout} variant="outline" small style={{ width: "100%", justifyContent: "center" }}>
          Sign Out
        </Btn>
      </div>
    </div>
  );
}

function TopBar({ user, onLogout }) {
  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 56,
        background: "#fff",
        borderBottom: "1px solid #e8ecf0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 16px",
        zIndex: 200,
        boxShadow: "0 1px 8px rgba(0,0,0,.06)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <img src="/logo.svg" alt="Perfect Tech" style={{ height: 22, display: "block" }} />
        <div style={{ fontSize: 10, fontWeight: 600, color: "#aaa", letterSpacing: 0.4, textTransform: "uppercase", paddingTop: 1 }}>Installer</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#333", lineHeight: 1.2 }}>{user?.name}</div>
        </div>
        <button
          onClick={onLogout}
          style={{
            background: "#f5f7fa",
            border: "1px solid #e0e4ea",
            borderRadius: 8,
            padding: "6px 12px",
            fontSize: 12,
            fontWeight: 600,
            color: "#555",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Sign Out
        </button>
      </div>
    </header>
  );
}

function BottomNav() {
  const activeTab = useActiveNavId();
  return (
    <nav
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        background: "#fff",
        borderTop: "1px solid #e8ecf0",
        display: "flex",
        zIndex: 200,
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        boxShadow: "0 -2px 12px rgba(0,0,0,.06)",
      }}
    >
      {NAV.map((n) => {
        const active = activeTab === n.id;
        return (
          <NavLink
            key={n.id}
            to={n.path}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "8px 4px 10px",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: active ? BLUE : "#999",
              fontSize: 10,
              fontWeight: active ? 700 : 400,
              gap: 3,
              minWidth: 0,
              textDecoration: "none",
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.2 : 1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d={n.icon} />
            </svg>
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
              {n.label}
            </span>
          </NavLink>
        );
      })}
    </nav>
  );
}

// ─── Available Jobs ─────────────────────────────────────────────────────────
function AvailableJobs() {
  const { api, toast } = useOutletContext();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taking, setTaking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/orders/available?per_page=50&sort=asc");
      const sorted = (res.data.data || []).sort((a, b) => new Date(a.installation_date) - new Date(b.installation_date));
      setJobs(sorted);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [api, toast]);

  useEffect(() => { load(); }, [load]);

  const take = async (id) => {
    setTaking(id);
    try {
      const res = await api.post(`/orders/${id}/take`);
      toast(res.message || "Job accepted! Check My Jobs.", "success");
      load();
    } catch (e) {
      toast(e.message, "error");
      load();
    } finally {
      setTaking(null);
    }
  };

  const blockLabel = (j) => {
    if (j.block_message) return j.block_message;
    if (j.block_reason === "slot_conflict") {
      return "Already booked this slot at another address";
    }
    if (j.block_reason === "too_far") {
      const km = j.distance_km != null ? ` (~${j.distance_km} km)` : "";
      return `Too far from previous timeslot job${km}`;
    }
    return "Not available for you right now";
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111" }}>Available Jobs</h2>
          <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>Shown to all zone installers · accept depends on your schedule</div>
        </div>
        <Btn onClick={load} variant="ghost" small>Refresh</Btn>
      </div>
      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}><Spinner size={28} /></div>
      ) : jobs.length === 0 ? (
        <Card style={{ textAlign: "center", color: "#888", padding: 60 }}>No available jobs right now.</Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {jobs.map((j) => {
            const blocked = j.can_take === false;
            return (
              <Card key={j.id} style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "flex-start", gap: 16, opacity: blocked ? 0.85 : 1 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: "#111" }}>#{j.order_number}</span>
                    <Badge color="amber">{j.installation_slot}</Badge>
                    {blocked && <Badge color="red">Unavailable for you</Badge>}
                  </div>
                  <div style={{ fontSize: 14, color: "#333", marginBottom: 4 }}>{j.customer_name} · {j.customer_phone_number}</div>
                  <div style={{ fontSize: 13, color: "#666" }}>
                    {j.address?.name} — {j.unit_no}, Floor {j.floor_no}<br />
                    {j.address?.address}<br />
                    <span style={{ color: "#888" }}>📅 {j.installation_date} · {j.address?.area?.zone?.name}</span>
                  </div>
                  {j.installation_notes && (
                    <div style={{ marginTop: 6, fontSize: 12, color: BLUE, background: "#e8f0ff", padding: "4px 10px", borderRadius: 6, display: "inline-block" }}>
                      📝 {j.installation_notes}
                    </div>
                  )}
                  {blocked && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "#b71c1c", background: "#fdecea", padding: "6px 10px", borderRadius: 6 }}>
                      {blockLabel(j)}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: isMobile ? "row" : "column", gap: 8, alignItems: isMobile ? "center" : "flex-end", width: isMobile ? "100%" : "auto" }}>
                  <Btn onClick={() => navigate(`/jobs/${j.id}`)} variant="ghost" small style={isMobile ? { flex: 1, justifyContent: "center" } : {}}>Details</Btn>
                  <Btn onClick={() => take(j.id)} disabled={blocked || taking === j.id} small style={isMobile ? { flex: 1, justifyContent: "center" } : {}}>
                    {taking === j.id ? <Spinner size={14} /> : "Accept Job"}
                  </Btn>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── My Jobs ────────────────────────────────────────────────────────────────
function MyJobs() {
  const { api, toast } = useOutletContext();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter !== "all" ? `?status=${filter}` : "";
      const res = await api.get(`/orders/mine${q}`);
      setJobs(res.data.data || []);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [api, filter, toast]);

  useEffect(() => { load(); }, [load]);

  const FILTERS = ["all", "pending", "installing", "completed", "cancelled"];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111" }}>My Jobs</h2>
          <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>All accepted orders</div>
        </div>
        <Btn onClick={load} variant="ghost" small>Refresh</Btn>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: "5px 14px",
              borderRadius: 20,
              border: `1px solid ${filter === f ? BLUE : "#ddd"}`,
              background: filter === f ? BLUE : "#fff",
              color: filter === f ? "#fff" : "#555",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              textTransform: "capitalize",
            }}
          >
            {f}
          </button>
        ))}
      </div>
      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}><Spinner size={28} /></div>
      ) : jobs.length === 0 ? (
        <Card style={{ textAlign: "center", color: "#888", padding: 60 }}>No jobs found.</Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {jobs.map((j) => (
            <Card key={j.id} style={{ display: "flex", alignItems: "center", gap: 16, cursor: "pointer" }} onClick={() => navigate(`/jobs/${j.id}`)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, color: "#111" }}>#{j.order_number}</span>
                  <Badge color={statusColor(j.status)}>{j.status}</Badge>
                </div>
                <div style={{ fontSize: 13, color: "#555", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.address?.name} · {j.address?.address}</div>
                {j.assignedAccount && (
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                    Account: {j.assignedAccount.account_number} · {j.assignedAccount.username}
                  </div>
                )}
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M9 18l6-6-6-6" />
              </svg>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Add-on Jobs (mesh module, CCTV, etc.) ────────────────────────────────────
function AddonJobs() {
  const { api, toast } = useOutletContext();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("available");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taking, setTaking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const path = tab === "available" ? "/addon-orders/available?per_page=50" : "/addon-orders/mine?per_page=50";
      const res = await api.get(path);
      setJobs(res.data.data || []);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [api, tab, toast]);

  useEffect(() => { load(); }, [load]);

  const take = async (id) => {
    setTaking(id);
    try {
      const res = await api.post(`/addon-orders/${id}/take`);
      toast(res.message || "Job accepted! Check the Mine tab.", "success");
      load();
    } catch (e) {
      toast(e.message, "error");
      load();
    } finally {
      setTaking(null);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111" }}>Add-on Jobs</h2>
          <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>Fit mesh modules, CCTV and other add-ons for existing customers</div>
        </div>
        <Btn onClick={load} variant="ghost" small>Refresh</Btn>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[["available", "Available"], ["mine", "Mine"]].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            style={{
              padding: "5px 14px",
              borderRadius: 20,
              border: `1px solid ${tab === id ? BLUE : "#ddd"}`,
              background: tab === id ? BLUE : "#fff",
              color: tab === id ? "#fff" : "#555",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}><Spinner size={28} /></div>
      ) : jobs.length === 0 ? (
        <Card style={{ textAlign: "center", color: "#888", padding: 60 }}>
          {tab === "available" ? "No add-on jobs available right now." : "You haven't taken any add-on jobs yet."}
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {jobs.map((j) => (
            <Card key={j.id} style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", gap: 14 }}>
              <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => navigate(`/addon-jobs/${j.id}`)}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, fontSize: 15, color: "#111" }}>{j.addon?.name}{j.quantity > 1 ? ` x${j.quantity}` : ""}</span>
                  <Badge color={ADDON_JOB_STATUS_COLOR[j.status] || "gray"}>{ADDON_JOB_STATUS_LABEL[j.status] || j.status}</Badge>
                  <span style={{ fontWeight: 700, fontSize: 13, color: "#1a7f3c" }}>RM {Number(j.monthly_total).toFixed(2)}/mo</span>
                </div>
                <div style={{ fontSize: 13, color: "#555" }}>{j.address?.name} — {j.address?.unit_no ? `Unit ${j.address.unit_no}` : ""}{j.address?.floor_no ? `, Floor ${j.address.floor_no}` : ""}</div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{j.address?.address}</div>
                <div style={{ fontSize: 12, color: BLUE, marginTop: 4 }}>{j.address?.zone} · Account {j.account?.account_number}</div>
                {j.customer_notes && (
                  <div style={{ marginTop: 6, fontSize: 12, color: BLUE, background: "#e8f0ff", padding: "4px 10px", borderRadius: 6, display: "inline-block" }}>
                    📝 {j.customer_notes}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", width: isMobile ? "100%" : "auto" }}>
                <Btn onClick={() => navigate(`/addon-jobs/${j.id}`)} variant="ghost" small style={isMobile ? { flex: 1, justifyContent: "center" } : {}}>Details</Btn>
                {tab === "available" && (
                  <Btn onClick={() => take(j.id)} disabled={taking === j.id} small style={isMobile ? { flex: 1, justifyContent: "center" } : {}}>
                    {taking === j.id ? <Spinner size={14} /> : "Accept Job"}
                  </Btn>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function AddonJobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { api, toast } = useOutletContext();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/addon-orders/${jobId}`);
      setJob(res.data);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [api, jobId, toast]);

  useEffect(() => { load(); }, [load]);

  const doTake = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/addon-orders/${jobId}/take`);
      toast(res.message || "Job accepted!", "success");
      setJob(res.data);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const doComplete = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/addon-orders/${jobId}/complete`, notes ? { notes } : {});
      toast(res.message || "Installed! Billing has started.", "success");
      load();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) return toast("Please describe why this job can't be completed", "error");
    setBusy(true);
    try {
      const res = await api.post(`/addon-orders/${jobId}/reject`, { reason: rejectReason.trim() });
      toast(res.message || "Job declined.", "success");
      setJob(res.data);
      setShowReject(false);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div style={{ textAlign: "center", padding: 80 }}><Spinner size={32} /></div>;
  if (!job) return null;

  const mine = job.status !== "pending";

  return (
    <div>
      <button onClick={() => navigate("/addon-jobs")} style={{ background: "none", border: "none", cursor: "pointer", color: BLUE, fontSize: 14, fontWeight: 600, marginBottom: 16, display: "flex", alignItems: "center", gap: 6, padding: 0 }}>
        ← Back
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111" }}>{job.addon?.name}{job.quantity > 1 ? ` x${job.quantity}` : ""}</h2>
        <Badge color={ADDON_JOB_STATUS_COLOR[job.status] || "gray"}>{ADDON_JOB_STATUS_LABEL[job.status] || job.status}</Badge>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 560 }}>
        <Card>
          <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>Job Details</h4>
          {[
            ["Add-on", `${job.addon?.name}${job.quantity > 1 ? ` x${job.quantity}` : ""}`],
            ["Monthly total", `RM ${Number(job.monthly_total).toFixed(2)}`],
            ["Account", job.account?.account_number],
            ["Customer", job.customer_name],
            ["Phone", job.customer_phone],
          ].map(([l, v]) => v && (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "8px 0", borderBottom: "1px solid #f4f4f4", gap: 16 }}>
              <span style={{ color: "#888", flexShrink: 0 }}>{l}</span>
              <span style={{ fontWeight: 600, textAlign: "right" }}>{v}</span>
            </div>
          ))}
          {job.customer_notes && (
            <div style={{ marginTop: 10, padding: "8px 10px", background: "#fffbe6", borderRadius: 6, fontSize: 12, color: "#7a5c00", border: "1px solid #ffe58f" }}>
              📝 Customer note: {job.customer_notes}
            </div>
          )}
        </Card>

        <Card>
          <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>Address</h4>
          <div style={{ fontSize: 13, color: "#555", lineHeight: 1.8 }}>
            <strong>{job.address?.name}</strong><br />
            {(job.address?.floor_no || job.address?.unit_no) && (
              <span>
                {job.address?.floor_no ? `Floor ${job.address.floor_no}` : ""}{job.address?.floor_no && job.address?.unit_no ? ", " : ""}
                {job.address?.unit_no ? `Unit ${job.address.unit_no}` : ""}<br />
              </span>
            )}
            {job.address?.address}<br />
            <span style={{ color: BLUE }}>{job.address?.zone}</span>
          </div>
        </Card>

        {job.status === "pending" && (
          <Card>
            <p style={{ fontSize: 14, color: "#555", marginTop: 0, marginBottom: 14 }}>
              Accept this job to schedule a visit and fit the device.
            </p>
            <Btn onClick={doTake} disabled={busy}>{busy ? <Spinner size={14} /> : "Accept Job →"}</Btn>
          </Card>
        )}

        {job.status === "assigned" && mine && (
          <Card>
            <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>Confirm Installation</h4>
            <p style={{ fontSize: 13, color: "#555", marginTop: 0, marginBottom: 14 }}>
              Once the device is fitted and working, confirm below. This starts billing for the customer.
            </p>
            <Label>Notes (optional)</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical", marginBottom: 14 }} placeholder="e.g. Fitted in living room, signal confirmed strong" />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Btn onClick={doComplete} disabled={busy}>{busy ? <Spinner size={14} /> : "Confirm Installed ✓"}</Btn>
              <Btn onClick={() => setShowReject((v) => !v)} variant="ghost" disabled={busy}>Can't complete this job</Btn>
            </div>
            {showReject && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #f0f0f0" }}>
                <Label>Reason</Label>
                <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={2} style={{ ...inputStyle, resize: "vertical", marginBottom: 10 }} placeholder="e.g. No signal reception, customer unavailable" />
                <Btn onClick={doReject} variant="danger" disabled={busy} small>{busy ? <Spinner size={12} /> : "Decline Job"}</Btn>
              </div>
            )}
          </Card>
        )}

        {job.status === "completed" && (
          <Card style={{ textAlign: "center", padding: 40 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🎉</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#1a7f3c", marginBottom: 6 }}>Installed!</div>
            <div style={{ fontSize: 13, color: "#555" }}>Billing has started for this add-on.</div>
          </Card>
        )}

        {job.status === "rejected" && (
          <Card style={{ textAlign: "center", padding: 40 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🚫</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#c0392b", marginBottom: 6 }}>Job Declined</div>
            <div style={{ fontSize: 13, color: "#555" }}>{job.rejection_reason || "This job was declined."}</div>
          </Card>
        )}
      </div>
    </div>
  );
}

// ─── Signature Pad ──────────────────────────────────────────────────────────
function SignaturePad({ canvasRef, onDraw }) {
  const isDrawing = useRef(false);
  const lastPos = useRef(null);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src = e.touches?.[0] || e;
    return {
      x: (src.clientX - rect.left) * (canvas.width / rect.width),
      y: (src.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    isDrawing.current = true;
    lastPos.current = getPos(e, canvasRef.current);
    onDraw?.();
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#111";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    lastPos.current = pos;
  };

  const stopDraw = () => { isDrawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={800}
        height={220}
        style={{
          border: "1px solid #d0d7e3",
          borderRadius: 8,
          width: "100%",
          touchAction: "none",
          background: "#fff",
          cursor: "crosshair",
          display: "block",
        }}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={stopDraw}
        onMouseLeave={stopDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={stopDraw}
      />
      <button
        type="button"
        onClick={clear}
        style={{ marginTop: 6, background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: 12, padding: 0, textDecoration: "underline" }}
      >
        Clear signature
      </button>
    </div>
  );
}

// ─── Modal shell ────────────────────────────────────────────────────────────
function Modal({ children, onClose, width = 460 }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,20,30,.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 500,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: "24px 24px",
          width: "100%",
          maxWidth: width,
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 12px 48px rgba(0,0,0,.25)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

const selectStyle = { ...inputStyle };

// ─── Cancel Installation Modal ──────────────────────────────────────────────
function CancelInstallationModal({ jobId, api, toast, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [competitors, setCompetitors] = useState([]);
  const [loadingCompetitors, setLoadingCompetitors] = useState(false);
  const [competitorChoice, setCompetitorChoice] = useState(""); // competitor id or OTHERS_COMPETITOR_ID
  const [competitorOther, setCompetitorOther] = useState("");
  const [contactBackPeriod, setContactBackPeriod] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (reason === "competitor" && competitors.length === 0 && !loadingCompetitors) {
      setLoadingCompetitors(true);
      api
        .get("/competitors")
        .then((res) => setCompetitors(res.data || []))
        .catch(() => setErr("Failed to load competitor list."))
        .finally(() => setLoadingCompetitors(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reason]);

  const isOthersCompetitor = competitorChoice === OTHERS_COMPETITOR_ID;

  const valid = (() => {
    if (!reason) return false;
    if (reason === "competitor") {
      const hasCompetitor = isOthersCompetitor ? competitorOther.trim().length > 0 : !!competitorChoice;
      return hasCompetitor && !!contactBackPeriod;
    }
    if (reason === "other") return notes.trim().length > 0;
    return true;
  })();

  const submit = async () => {
    setErr("");
    if (!valid) {
      setErr("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { reason, notes: notes.trim() || undefined };
      if (reason === "competitor") {
        if (isOthersCompetitor) {
          payload.competitor_other = competitorOther.trim();
        } else {
          payload.competitor_id = Number(competitorChoice);
        }
        payload.contact_back_period = contactBackPeriod;
      }
      const res = await api.post(`/orders/${jobId}/cancel`, payload);
      toast(res.message || "Installation cancelled.", "success");
      onCancelled(res.data?.order);
    } catch (e) {
      setErr(e.message || "Failed to cancel installation.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={submitting ? () => {} : onClose}>
      <h3 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: "#111" }}>Cancel Installation</h3>
      <p style={{ margin: "0 0 18px", fontSize: 13, color: "#888" }}>
        This will cancel the order and record the reason for follow-up.
      </p>

      <div style={{ marginBottom: 16 }}>
        <Label>Reason *</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
          {CANCEL_REASONS.map((r) => (
            <label
              key={r.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                border: `1px solid ${reason === r.id ? BLUE : "#e0e4ea"}`,
                background: reason === r.id ? "#f0f5ff" : "#fff",
                borderRadius: 8,
                cursor: "pointer",
                fontSize: 13.5,
                fontWeight: reason === r.id ? 600 : 400,
                color: reason === r.id ? BLUE : "#333",
              }}
            >
              <input
                type="radio"
                name="cancel_reason"
                value={r.id}
                checked={reason === r.id}
                onChange={() => setReason(r.id)}
                style={{ accentColor: BLUE }}
              />
              {r.label}
            </label>
          ))}
        </div>
      </div>

      {reason === "competitor" && (
        <div style={{ marginBottom: 16, padding: "14px 14px", background: "#f8faff", borderRadius: 10, border: "1px solid #e8ecf0" }}>
          <Label>Which competitor? *</Label>
          {loadingCompetitors ? (
            <div style={{ padding: "10px 0" }}><Spinner size={16} /></div>
          ) : (
            <select
              value={competitorChoice}
              onChange={(e) => setCompetitorChoice(e.target.value)}
              style={{ ...selectStyle, marginBottom: isOthersCompetitor ? 10 : 14 }}
            >
              <option value="">Select competitor…</option>
              {competitors.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
              <option value={OTHERS_COMPETITOR_ID}>Others: fill in</option>
            </select>
          )}
          {isOthersCompetitor && (
            <input
              value={competitorOther}
              onChange={(e) => setCompetitorOther(e.target.value)}
              placeholder="Name the competitor"
              style={{ ...inputStyle, marginBottom: 14 }}
            />
          )}
          <Label>Contact back in *</Label>
          <select
            value={contactBackPeriod}
            onChange={(e) => setContactBackPeriod(e.target.value)}
            style={selectStyle}
          >
            <option value="">Select timeframe…</option>
            {CONTACT_BACK_OPTIONS.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <div style={{ fontSize: 11, color: "#888", marginTop: 6 }}>
            {contactBackPeriod
              ? `This customer will be added to the lead list — follow up in ${contactBackLabel(contactBackPeriod)}.`
              : "This customer will be added to the lead list for a follow-up call."}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <Label>Notes {reason === "other" ? "*" : "(optional)"}</Label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder={reason === "other" ? "Describe the reason for cancelling…" : "Any additional remarks…"}
          style={{ ...inputStyle, resize: "vertical", marginTop: 6 }}
        />
      </div>

      {err && <div style={{ color: "#c0392b", fontSize: 13, marginBottom: 14 }}>{err}</div>}

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn onClick={onClose} variant="outline" disabled={submitting}>Back</Btn>
        <Btn onClick={submit} variant="danger" disabled={!valid || submitting}>
          {submitting ? <Spinner size={14} /> : "Confirm Cancellation"}
        </Btn>
      </div>
    </Modal>
  );
}

// ─── Job Detail / Installation Wizard ───────────────────────────────────────
function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { api, toast } = useOutletContext();
  const isMobile = useIsMobile();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  // steps: 0=info, 1=start, 2=onu-auth, 3=wifi, 4=verify-id, 5=upload-proof, completed
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  // form state
  const [serial, setSerial] = useState("");
  const [ssid2g, setSsid2g] = useState("");
  const [pass2g, setPass2g] = useState("");
  const [ssid5g, setSsid5g] = useState("");
  const [pass5g, setPass5g] = useState("");
  const [icNumber, setIcNumber] = useState("");
  const [hasSig, setHasSig] = useState(false);
  const sigCanvasRef = useRef(null);
  const [modemImg, setModemImg] = useState(null);
  const [speedImg, setSpeedImg] = useState(null);
  const [notes, setNotes] = useState("");
  const [onuStatus, setOnuStatus] = useState(null);
  const [connRefreshing, setConnRefreshing] = useState(false);
  const [portUnavailable, setPortUnavailable] = useState(null);
  // data from /start and /new-port
  const [startResult, setStartResult] = useState(null);
  const [portChain, setPortChain] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/orders/${jobId}`);
      const o = res.data;
      setOrder(o);
      if (o.status === "completed") setStep(5);
      else if (o.status === "installing") {
        const ACTION_STEP = { authorize_onu: 2, configure_wifi: 3, upload_proof: 4, verify_id: 5, complete: 4, completed: 5 };
        setStep(ACTION_STEP[o.next_action] ?? 2);
      } else if (o.installer_id) setStep(1);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [api, jobId, toast]);

  useEffect(() => { load(); }, [load]);

  // Passport holders: verify-id is not required, account activates on /complete
  // No auto-skip needed — doComplete handles it via next_action from the response

  const handleIcInput = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 12);
    let formatted = digits;
    if (digits.length > 8) formatted = `${digits.slice(0, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
    else if (digits.length > 6) formatted = `${digits.slice(0, 6)}-${digits.slice(6)}`;
    setIcNumber(formatted);
  };

  const doStart = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/orders/${jobId}/start`);
      setStartResult(res.data);
      setPortChain(res.data.account?.port_chain);
      setOrder((o) => ({ ...o, status: "installing" }));
      setStep(2);
      toast(res.message || "Installation started! Connect fibre to the assigned port.", "success");
    } catch (e) {
      if (e.data?.port_unavailability) {
        setPortUnavailable(e.data.port_unavailability);
      } else {
        toast(e.message, "error");
      }
    } finally {
      setBusy(false);
    }
  };

  const doAuthorize = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/orders/${jobId}/onu/authorize`, { serial_number: serial });
      if (res.data.wan_error) {
        toast(res.message || `ONU authorized. WAN issue: ${res.data.wan_error} — you can retry WAN separately.`, "error");
      } else {
        toast(res.message || "ONU authorized and PPPoE WAN configured!", "success");
      }
      setStep(3);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const doNewPort = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/orders/${jobId}/new-port`);
      const d = res.data;
      setPortChain(d.port_chain);
      toast(res.message || `New port assigned: ${d.new_dp.name} port ${d.new_dp.splitter_port}`, "success");
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const ACTION_STEP = { authorize_onu: 2, configure_wifi: 3, upload_proof: 4, verify_id: 5, complete: 4, completed: 5 };

  const goToNextAction = (res) => {
    const next = res?.next_action || res?.data?.next_action;
    setStep(ACTION_STEP[next] ?? 4);
    return next;
  };

  const doWifi = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/orders/${jobId}/wifi`, {
        ssid_2g: ssid2g, password_2g: pass2g,
        ssid_5g: ssid5g || undefined, password_5g: pass5g || undefined,
      });
      toast(res.message || "WiFi configured!", "success");
      goToNextAction(res);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const doSkipWifi = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/orders/${jobId}/wifi`, { skip: true });
      toast(res.message || "WiFi skipped.", "success");
      goToNextAction(res);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const doVerifyId = async () => {
    if (!icNumber.trim()) return toast(`Please enter the customer's ${order?.customer_identification_type === "passport" ? "passport" : "IC"} number`, "error");
    if (!hasSig) return toast("Please collect the customer's signature", "error");
    setBusy(true);
    try {
      const blob = await new Promise((resolve) => sigCanvasRef.current.toBlob(resolve, "image/png"));
      const fd = new FormData();
      fd.append("ic_number", icNumber.replace(/[\s-]/g, ""));
      fd.append("signature", blob, "signature.png");
      const res = await api.postForm(`/orders/${jobId}/verify-id`, fd);
      toast(res.message || "ID verified! Account activated and payment credited.", "success");
      setOrder((o) => ({ ...o, status: "completed", installation_signature_collected: true, id_verified_at: res.data?.id_verified_at || new Date().toISOString() }));
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const doRefreshConn = async () => {
    setConnRefreshing(true);
    try {
      const res = await api.get(`/orders/${jobId}/connection-status`);
      setOrder((o) => ({
        ...o,
        connection: {
          ...res.data,
          // merge optical: fresh data wins, fall back to previous/account for fields the endpoint omits
          optical: res.data?.optical
            ? {
                onu_serial_number:
                  o.connection?.optical?.onu_serial_number ||
                  o.assignedAccount?.onu_serial_number,
                onu_status:
                  o.connection?.optical?.onu_status,
                ...res.data.optical,
              }
            : o.connection?.optical,
        },
      }));
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setConnRefreshing(false);
    }
  };

  const doCheckOnu = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/orders/${jobId}/onu/status`);
      setOnuStatus(res.data);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const doComplete = async () => {
    if (!modemImg || !speedImg) return toast("Both images required", "error");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("modem_image", modemImg);
      fd.append("speedtest_image", speedImg);
      if (notes) fd.append("notes", notes);
      const res = await api.postForm(`/orders/${jobId}/complete`, fd);
      const next = res.next_action || res.data?.next_action;
      if (next === "verify_id") {
        toast(res.message || "Photos uploaded! Now verify the customer's ID to activate the account.", "success");
        setStep(5);
      } else if (next === "completed") {
        toast(res.message || "Job completed! Payment credited.", "success");
        setOrder((o) => ({ ...o, status: "completed" }));
      } else {
        toast(res.message || "Photos uploaded.", "success");
        goToNextAction(res);
      }
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div style={{ textAlign: "center", padding: 80 }}><Spinner size={32} /></div>;
  if (!order) return null;

  const STEPS = ["Overview", "Start", "ONU Auth", "WiFi", "Upload Proof", "Verify ID"];
  const isInstalling = order.status === "installing";

  const acct = startResult?.account || order.assignedAccount;
  const activePortChain = portChain || acct?.port_chain || order.port_chain;

  return (
    <div>
      <button onClick={() => navigate("/myjobs")} style={{ background: "none", border: "none", cursor: "pointer", color: BLUE, fontSize: 14, fontWeight: 600, marginBottom: 16, display: "flex", alignItems: "center", gap: 6, padding: 0 }}>
        ← Back
      </button>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111" }}>#{order.order_number}</h2>
        <Badge color={statusColor(order.status)}>{order.status}</Badge>
        {order.installer_id && order.status !== "completed" && order.status !== "cancelled" && (
          <Btn onClick={() => setShowCancelModal(true)} variant="danger" small style={{ marginLeft: "auto" }}>
            Cancel Installation
          </Btn>
        )}
      </div>

      {showCancelModal && (
        <CancelInstallationModal
          jobId={jobId}
          api={api}
          toast={toast}
          onClose={() => setShowCancelModal(false)}
          onCancelled={(updatedOrder) => {
            setShowCancelModal(false);
            setOrder((o) => ({ ...o, ...(updatedOrder || {}), status: "cancelled" }));
          }}
        />
      )}

      {/* stepper */}
      {isInstalling || order.status === "completed" ? (
        <div style={{ overflowX: "auto", marginBottom: 24, paddingBottom: 4 }}>
          <div style={{ display: "flex", alignItems: "center", minWidth: isMobile ? 480 : "auto" }}>
            {STEPS.map((s, i) => (
              <div key={s} style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? 1 : 0 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer" }} onClick={() => isInstalling && i <= step && setStep(i)}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                    background: i < step ? BLUE : i === step ? BLUE : "#e8ecf0",
                    color: i <= step ? "#fff" : "#aaa", fontSize: 12, fontWeight: 700,
                  }}>
                    {i < step ? "✓" : i + 1}
                  </div>
                  <div style={{ fontSize: 10, color: i === step ? BLUE : "#aaa", marginTop: 4, fontWeight: i === step ? 700 : 400, whiteSpace: "nowrap" }}>{s}</div>
                </div>
                {i < STEPS.length - 1 && <div style={{ flex: 1, height: 2, background: i < step ? BLUE : "#e8ecf0", marginBottom: 14 }} />}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 20, alignItems: "flex-start" }}>
        {/* main panel */}
        <div style={{ flex: 1, minWidth: 0, width: "100%" }}>
          {order.status === "cancelled" ? (
            <Card style={{ textAlign: "center", padding: 48 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🚫</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#c0392b", marginBottom: 10 }}>Installation Cancelled</div>
              <div style={{ display: "inline-block", textAlign: "left", fontSize: 13, color: "#555", background: "#fdecea", borderRadius: 10, padding: "14px 18px", border: "1px solid #f5c6c6" }}>
                <div style={{ marginBottom: 6 }}><strong>Reason:</strong> {reasonLabel(order.cancellation_reason)}</div>
                {order.cancellation_notes && <div style={{ marginBottom: 6 }}><strong>Notes:</strong> {order.cancellation_notes}</div>}
                {order.cancelled_at && <div style={{ color: "#888" }}>Cancelled {new Date(order.cancelled_at).toLocaleString()}</div>}
              </div>
            </Card>
          ) : (
          <>
          {step === 0 && (
            <Card>
              <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>Customer Details</h3>
              <table style={{ width: "100%", fontSize: 14, borderCollapse: "collapse" }}>
                {[
                  ["Name", order.customer_name],
                  ["Phone", order.customer_phone_number],
                  ["IC/Passport", order.customer_identification_number],
                  ["Unit", `${order.unit_no}, Floor ${order.floor_no}`],
                  ["Date", order.installation_date],
                  ["Slot", order.installation_slot],
                ].map(([l, v]) => v && (
                  <tr key={l} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "8px 0", color: "#888", width: 130, verticalAlign: "top" }}>{l}</td>
                    <td style={{ padding: "8px 0", fontWeight: 500 }}>{v}</td>
                  </tr>
                ))}
              </table>
              {order.installation_notes && (
                <div style={{ marginTop: 14, padding: "10px 14px", background: "#fffbe6", borderRadius: 8, fontSize: 13, color: "#7a5c00", border: "1px solid #ffe58f" }}>
                  📝 {order.installation_notes}
                </div>
              )}
              {order.status === "pending" && order.installer_id && (
                <div style={{ marginTop: 16 }}>
                  <Btn onClick={() => setStep(1)}>Start Installation →</Btn>
                </div>
              )}
            </Card>
          )}

          {step === 1 && (
            <Card>
              <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>Start Installation</h3>

              {portUnavailable ? (
                <div>
                  <div style={{ padding: "16px 18px", background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 10, marginBottom: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 20 }}>⚠️</span>
                      <span style={{ fontWeight: 700, fontSize: 15, color: "#7a5c00" }}>No Port Available</span>
                    </div>
                    <p style={{ margin: "0 0 10px", fontSize: 13, color: "#7a5c00", lineHeight: 1.6 }}>
                      There are no available fibre ports for this address. HQ has been notified and needs to resolve this before installation can begin.
                    </p>
                    {portUnavailable.notes && (
                      <div style={{ fontSize: 12, color: "#9a7500", background: "#fff3c4", borderRadius: 6, padding: "6px 10px", marginBottom: 10 }}>
                        {portUnavailable.notes}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "#aaa" }}>
                      Reported {new Date(portUnavailable.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: "#888", marginBottom: 14 }}>Contact your HQ to get this resolved, then try again.</div>
                  <Btn onClick={() => { setPortUnavailable(null); doStart(); }} disabled={busy} variant="ghost">
                    {busy ? <Spinner size={14} /> : "↺ Try Again"}
                  </Btn>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: 14, color: "#555", marginTop: 0, marginBottom: 20 }}>
                    Press Start to create the PPPoE account and get your assigned DP port. You'll enter the modem serial number in the next step.
                  </p>
                  <Btn onClick={doStart} disabled={busy}>
                    {busy ? <Spinner size={14} /> : "Start Installation →"}
                  </Btn>
                </div>
              )}
            </Card>
          )}

          {step === 2 && (
            <Card>
              <h3 style={{ margin: "0 0 14px", fontSize: 16, fontWeight: 700 }}>Authorize ONU</h3>

              {activePortChain && (
                <div style={{ padding: "12px 16px", background: "#e8f0ff", borderRadius: 8, marginBottom: 16, border: `1px solid ${BLUE}22` }}>
                  <div style={{ fontSize: 12, color: BLUE, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>Connect Fibre To</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#111" }}>{activePortChain.connect_to}</div>
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: "#888", marginBottom: 8 }}>Signal weak or fibre not connecting?</div>
                <Btn onClick={doNewPort} variant="ghost" small disabled={busy}>
                  {busy ? <Spinner size={12} /> : "⇄ Request New Port"}
                </Btn>
                {onuStatus && (
                  <div style={{ marginTop: 10, padding: "10px 14px", background: onuStatus.signal_ok ? "#e6f4ea" : "#fdecea", borderRadius: 8, fontSize: 13 }}>
                    Signal: <strong>{onuStatus.rx_power_dbm} dBm</strong> · {onuStatus.run_state} {onuStatus.signal_ok ? "✅" : "⚠️"}
                  </div>
                )}
                <button onClick={doCheckOnu} disabled={busy} style={{ marginTop: 8, background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: 12, textDecoration: "underline", padding: 0 }}>
                  Check live signal
                </button>
              </div>

              <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16, marginTop: 4 }}>
                <Label>Modem Serial Number</Label>
                <input
                  value={serial}
                  onChange={(e) => setSerial(e.target.value)}
                  placeholder="e.g. HWTC1234567890"
                  style={{ ...inputStyle, marginBottom: 14 }}
                />
                <Btn onClick={doAuthorize} disabled={!serial || busy}>
                  {busy ? <Spinner size={14} /> : "Authorize ONU →"}
                </Btn>
              </div>
            </Card>
          )}

          {step === 3 && (
            <Card>
              <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>Configure WiFi</h3>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 16 }}>
                <div>
                  <Label>SSID 2.4GHz</Label>
                  <input value={ssid2g} onChange={(e) => setSsid2g(e.target.value)} placeholder="e.g. PerfectHome" style={inputStyle} />
                </div>
                <div>
                  <Label>Password 2.4GHz</Label>
                  <input value={pass2g} onChange={(e) => setPass2g(e.target.value)} placeholder="min 8 chars" style={inputStyle} />
                </div>
                <div>
                  <Label>SSID 5GHz (optional)</Label>
                  <input value={ssid5g} onChange={(e) => setSsid5g(e.target.value)} placeholder="e.g. PerfectHome_5G" style={inputStyle} />
                </div>
                <div>
                  <Label>Password 5GHz (optional)</Label>
                  <input value={pass5g} onChange={(e) => setPass5g(e.target.value)} placeholder="min 8 chars" style={inputStyle} />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <Btn onClick={doWifi} disabled={!ssid2g || !pass2g || busy}>
                  {busy ? <Spinner size={14} /> : "Apply WiFi Settings →"}
                </Btn>
                <button onClick={doSkipWifi} disabled={busy} style={{ background: "none", border: "none", cursor: busy ? "not-allowed" : "pointer", color: "#888", fontSize: 13, padding: 0 }}>
                  Skip
                </button>
              </div>
            </Card>
          )}

          {step === 4 && order.status !== "completed" && (
            <Card>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 12 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Upload Proof</h3>
                <button
                  onClick={doRefreshConn}
                  disabled={connRefreshing}
                  style={{ background: "none", border: `1px solid #d0d7e3`, borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 600, color: connRefreshing ? "#aaa" : "#555", cursor: connRefreshing ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}
                >
                  {connRefreshing ? <Spinner size={12} /> : "↻"} Refresh Status
                </button>
              </div>

              {order.connection && (
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10, marginBottom: 20 }}>
                  <div style={{ padding: "12px 16px", borderRadius: 8, background: order.connection.pppoe?.is_connected ? "#e6f4ea" : "#fdecea", border: `1px solid ${order.connection.pppoe?.is_connected ? "#b7dfbf" : "#f5c6c6"}` }}>
                    <div style={{ fontSize: 11, color: "#888", fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>PPPoE</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: order.connection.pppoe?.is_connected ? "#1a7f3c" : "#c0392b", marginBottom: 4 }}>
                      {order.connection.pppoe?.is_connected ? "Connected ✅" : "Not Connected ❌"}
                    </div>
                    {order.connection.pppoe?.session?.ip_address && (
                      <div style={{ fontSize: 12, color: "#555" }}>IP: <strong>{order.connection.pppoe.session.ip_address}</strong></div>
                    )}
                    {order.connection.pppoe?.session?.started_at && (
                      <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                        Since {new Date(order.connection.pppoe.session.started_at).toLocaleTimeString()}
                      </div>
                    )}
                  </div>
                  <div style={{ padding: "12px 16px", borderRadius: 8, background: order.connection.optical?.signal_ok ? "#e6f4ea" : "#fdecea", border: `1px solid ${order.connection.optical?.signal_ok ? "#b7dfbf" : "#f5c6c6"}` }}>
                    <div style={{ fontSize: 11, color: "#888", fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Optical Signal</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: order.connection.optical?.signal_ok ? "#1a7f3c" : "#c0392b", marginBottom: 4 }}>
                      {order.connection.optical?.rx_power_dbm} dBm {order.connection.optical?.signal_ok ? "✅" : "⚠️"}
                    </div>
                    <div style={{ fontSize: 12, color: "#555" }}>ONU: <strong>{order.connection.optical?.onu_serial_number}</strong></div>
                    <div style={{ fontSize: 11, color: "#888", marginTop: 2, textTransform: "capitalize" }}>{order.connection.optical?.onu_status}</div>
                  </div>
                </div>
              )}

              <p style={{ fontSize: 14, color: "#555", marginTop: 0 }}>Upload modem and speed test photos. Customer ID verification is the final step.</p>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 16 }}>
                <div>
                  <Label>Modem Photo *</Label>
                  <input type="file" accept="image/*" capture="environment" onChange={(e) => setModemImg(e.target.files[0])} style={{ display: "block", marginTop: 8, fontSize: 13, width: "100%" }} />
                  {modemImg && <div style={{ fontSize: 12, color: "#1a7f3c", marginTop: 4 }}>✓ {modemImg.name}</div>}
                </div>
                <div>
                  <Label>Speed Test Screenshot *</Label>
                  <input type="file" accept="image/*" capture="screen" onChange={(e) => setSpeedImg(e.target.files[0])} style={{ display: "block", marginTop: 8, fontSize: 13, width: "100%" }} />
                  {speedImg && <div style={{ fontSize: 12, color: "#1a7f3c", marginTop: 4 }}>✓ {speedImg.name}</div>}
                </div>
              </div>
              <Label>Notes (optional)</Label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical", marginBottom: 16 }} placeholder="Any remarks..." />
              <Btn onClick={doComplete} disabled={!modemImg || !speedImg || busy}>
                {busy ? <Spinner size={14} /> : "Submit Photos →"}
              </Btn>
            </Card>
          )}

          {step === 5 && order.status !== "completed" && (
            <Card>
              <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Verify ID & Collect Signature</h3>
              <p style={{ fontSize: 14, color: "#555", marginTop: 0, marginBottom: 20 }}>
                Enter the customer's IC number and collect their signature. This activates the account and credits your wallet.
              </p>

              {order.id_verified_at && (
                <div style={{ padding: "12px 16px", background: "#e6f4ea", borderRadius: 8, marginBottom: 16, fontSize: 14, color: "#1a7f3c", fontWeight: 600 }}>
                  ✅ Verified at {new Date(order.id_verified_at).toLocaleTimeString()} — you can re-submit if needed.
                </div>
              )}

              <div style={{ marginBottom: 18 }}>
                <Label>IC Number *</Label>
                <input
                  value={icNumber}
                  onChange={(e) => handleIcInput(e.target.value)}
                  placeholder="e.g. 970808-14-5809"
                  inputMode="numeric"
                  style={{ ...inputStyle, fontFamily: "monospace", letterSpacing: 1 }}
                />
                <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>Spaces and dashes are ignored during verification.</div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <Label>Customer Signature *</Label>
                <div style={{ marginTop: 8 }}>
                  <SignaturePad canvasRef={sigCanvasRef} onDraw={() => setHasSig(true)} />
                  {hasSig && <div style={{ fontSize: 12, color: "#1a7f3c", marginTop: 4 }}>✓ Signature captured</div>}
                </div>
              </div>

              <Btn onClick={doVerifyId} disabled={!icNumber.trim() || !hasSig || busy}>
                {busy ? <Spinner size={14} /> : "Verify & Activate Account ✓"}
              </Btn>
            </Card>
          )}

          {step === 5 && order.status === "completed" && (
            <Card style={{ textAlign: "center", padding: 48 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#1a7f3c", marginBottom: 8 }}>Installation Completed!</div>
              <div style={{ fontSize: 14, color: "#555" }}>Account is active. Payment has been credited to your wallet.</div>
            </Card>
          )}
          </>
          )}
        </div>

        {/* side panel */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14, width: isMobile ? "100%" : 320, flexShrink: 0 }}>
          <Card>
            <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>Customer</h4>
            {[
              ["Name", order.customer_name],
              ["Phone", order.customer_phone_number],
              ["Date", order.installation_date ? new Date(order.installation_date).toLocaleDateString() : null],
              ["Slot", order.installation_slot],
            ].map(([l, v]) => v ? (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6, gap: 8 }}>
                <span style={{ color: "#888", flexShrink: 0 }}>{l}</span>
                <span style={{ fontWeight: 600, wordBreak: "break-all", textAlign: "right" }}>{v}</span>
              </div>
            ) : null)}
            <div style={{ borderTop: "1px solid #f0f0f0", marginTop: 8, paddingTop: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13 }}>
                <span style={{ color: "#888" }}>ID & Signature</span>
                <span style={{ fontWeight: 700, color: order.installation_signature_collected ? "#1a7f3c" : "#b36b00" }}>
                  {order.installation_signature_collected ? "✅ Done" : "⏳ Pending"}
                </span>
              </div>
            </div>
            {order.installation_notes && (
              <div style={{ marginTop: 8, padding: "8px 10px", background: "#fffbe6", borderRadius: 6, fontSize: 12, color: "#7a5c00", border: "1px solid #ffe58f" }}>
                📝 {order.installation_notes}
              </div>
            )}
          </Card>

          <Card>
            <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>Address</h4>
            <div style={{ fontSize: 13, color: "#555", lineHeight: 1.8 }}>
              <strong>{order.address?.name}</strong><br />
              {(order.floor_no || order.unit_no) && (
                <span>
                  {order.floor_no ? `Floor ${order.floor_no}` : ""}{order.floor_no && order.unit_no ? ", " : ""}
                  {order.unit_no ? `Unit ${order.unit_no}` : ""}
                </span>
              )}{(order.floor_no || order.unit_no) && <br />}
              {order.address?.address}<br />
              {order.address?.area?.city}, {order.address?.area?.zip}<br />
              <span style={{ color: BLUE }}>{order.address?.area?.zone?.name}</span>
            </div>
          </Card>

          {activePortChain && (
            <Card>
              <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>Fibre Port</h4>
              <div style={{ padding: "10px 14px", background: "#e8f0ff", borderRadius: 8, marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: BLUE, fontWeight: 700, marginBottom: 2 }}>Connect to</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111" }}>{activePortChain.connect_to}</div>
              </div>
              {order.connection?.optical && (
                <div style={{ padding: "8px 12px", borderRadius: 8, background: order.connection.optical.signal_ok ? "#e6f4ea" : "#fdecea", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: "#888", fontWeight: 700, textTransform: "uppercase", marginBottom: 3 }}>Optical Signal</div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: order.connection.optical.signal_ok ? "#1a7f3c" : "#c0392b" }}>
                    {order.connection.optical.rx_power_dbm} dBm {order.connection.optical.signal_ok ? "✅" : "⚠️"}
                  </div>
                  <div style={{ fontSize: 11, color: "#888", marginTop: 2, textTransform: "capitalize" }}>{order.connection.optical.onu_status}</div>
                </div>
              )}
              {isInstalling && (
                <Btn onClick={doNewPort} variant="ghost" small disabled={busy} style={{ width: "100%", justifyContent: "center" }}>
                  {busy ? <Spinner size={12} /> : "⇄ Request New Port"}
                </Btn>
              )}
            </Card>
          )}

          {acct && (
            <Card>
              <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>Account</h4>
              {[
                ["Account #", acct.account_number],
                ["Username", acct.username],
                ...(acct.password ? [["Password", acct.password]] : []),
                ["Status", acct.status],
              ].map(([l, v]) => v && (
                <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6, gap: 8 }}>
                  <span style={{ color: "#888", flexShrink: 0 }}>{l}</span>
                  <span style={{ fontWeight: 600, wordBreak: "break-all", textAlign: "right" }}>{v}</span>
                </div>
              ))}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Wallet ─────────────────────────────────────────────────────────────────
function Wallet() {
  const { api, toast } = useOutletContext();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/wallet").then((r) => { setData(r); setLoading(false); }).catch((e) => { toast(e.message, "error"); setLoading(false); });
  }, [api, toast]);

  if (loading) return <div style={{ textAlign: "center", padding: 80 }}><Spinner size={28} /></div>;
  if (!data) return null;

  const { balance, data: { data: txns } } = data;

  return (
    <div>
      <h2 style={{ margin: "0 0 20px", fontSize: 20, fontWeight: 700 }}>Wallet</h2>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 12, marginBottom: 24 }}>
        {[
          ["Total Income", balance.total_income, "#1a7f3c"],
          ["Withdrawn", balance.total_withdrawal, "#555"],
          ["Pending", balance.pending_withdrawal, "#b36b00"],
          ["Available", balance.available_balance, BLUE],
        ].map(([l, v, c]) => (
          <div key={l} style={{ background: "#fff", borderRadius: 10, border: "1px solid #e8ecf0", padding: isMobile ? "14px 12px" : "16px 18px" }}>
            <div style={{ fontSize: 11, color: "#888", fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>{l}</div>
            <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: c }}>{fmt(v)}</div>
          </div>
        ))}
      </div>
      <Card>
        <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 700 }}>Transaction History</h3>
        {txns?.length === 0 ? (
          <div style={{ color: "#888", fontSize: 14, textAlign: "center", padding: 30 }}>No transactions yet.</div>
        ) : (
          txns?.map((t) => (
            <div key={t.id} style={{ display: "flex", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f4f4f4" }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: t.type === "credit" ? "#e6f4ea" : "#fdecea", display: "flex", alignItems: "center", justifyContent: "center", marginRight: 12, flexShrink: 0, fontSize: 14 }}>
                {t.type === "credit" ? "↑" : "↓"}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{t.description}</div>
                <div style={{ fontSize: 11, color: "#aaa" }}>{new Date(t.created_at).toLocaleString()}</div>
              </div>
              <div style={{ fontWeight: 700, fontSize: 15, color: t.type === "credit" ? "#1a7f3c" : "#c0392b", flexShrink: 0, marginLeft: 8 }}>
                {t.type === "credit" ? "+" : "-"}{fmt(t.amount)}
              </div>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}

// ─── Withdrawals ─────────────────────────────────────────────────────────────
function Withdrawals() {
  const { api, toast } = useOutletContext();
  const isMobile = useIsMobile();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [wNotes, setWNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [balance, setBalance] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, wal] = await Promise.all([api.get("/withdrawals"), api.get("/wallet")]);
      setList(w.data.data || []);
      setBalance(wal.balance.available_balance);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [api, toast]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const cents = Math.round(parseFloat(amount) * 100);
    if (!cents || cents > balance) return toast("Invalid amount", "error");
    setSubmitting(true);
    try {
      const res = await api.post("/withdrawals", { amount: cents, notes: wNotes });
      toast(res.message || "Withdrawal request submitted!", "success");
      setAmount("");
      setWNotes("");
      load();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const STATUS_COLOR = { pending: "amber", approved: "blue", paid: "green", rejected: "red" };

  const requestForm = (
    <Card style={{ marginBottom: isMobile ? 16 : 0 }}>
      <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Request Withdrawal</h3>
      <div style={{ fontSize: 13, color: "#555", marginBottom: 14 }}>
        Available: <strong style={{ color: "#1a7f3c" }}>{fmt(balance)}</strong>
      </div>
      <Label>Amount (RM)</Label>
      <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 500.00" style={inputStyle} min="1" step="0.01" />
      <Label>Notes (optional)</Label>
      <input value={wNotes} onChange={(e) => setWNotes(e.target.value)} placeholder="e.g. Monthly payout" style={inputStyle} />
      <Btn onClick={submit} disabled={!amount || submitting} style={{ marginTop: 14, width: "100%", justifyContent: "center" }}>
        {submitting ? <Spinner size={14} /> : "Submit Request"}
      </Btn>
    </Card>
  );

  return (
    <div>
      <h2 style={{ margin: "0 0 20px", fontSize: 20, fontWeight: 700 }}>Withdrawals</h2>
      {isMobile && requestForm}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 340px", gap: 20 }}>
        <div>
          {loading ? (
            <div style={{ textAlign: "center", padding: 60 }}><Spinner size={28} /></div>
          ) : list.length === 0 ? (
            <Card style={{ textAlign: "center", color: "#888", padding: 50 }}>No withdrawals yet.</Card>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {list.map((w) => (
                <Card key={w.id} style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 16 }}>{fmt(w.amount)}</span>
                      <Badge color={STATUS_COLOR[w.status] || "gray"}>{w.status}</Badge>
                    </div>
                    {w.notes && <div style={{ fontSize: 13, color: "#555" }}>{w.notes}</div>}
                    {w.admin_notes && <div style={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>Admin: {w.admin_notes}</div>}
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{new Date(w.created_at).toLocaleString()}</div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
        {!isMobile && requestForm}
      </div>
    </div>
  );
}

// ─── Profile ─────────────────────────────────────────────────────────────────
function Profile() {
  const { user } = useOutletContext();
  const isMobile = useIsMobile();

  const fields = [
    ["Full Name", user.name],
    ["Email", user.email],
    ["Phone", user.phone],
    ["Bank Name", user.bank_name],
    ["Bank Account", user.bank_account_number],
  ].filter(([, v]) => v);

  const avatar = (size, fontSize) => (
    <div style={{ width: size, height: size, borderRadius: "50%", background: BLUE, display: "flex", alignItems: "center", justifyContent: "center", fontSize, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
      {user.name?.charAt(0)}
    </div>
  );

  return (
    <div>
      <h2 style={{ margin: "0 0 20px", fontSize: 20, fontWeight: 700 }}>Profile</h2>
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 20, alignItems: "flex-start" }}>

        <Card style={{ flex: 1, width: "100%" }}>
          {isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #f0f0f0" }}>
              {avatar(48, 20)}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 2 }}>{user.name}</div>
                <div style={{ fontSize: 13, color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</div>
                {user.company && <div style={{ marginTop: 4 }}><Badge color="blue">{user.company.name}</Badge></div>}
              </div>
            </div>
          )}
          {fields.map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "10px 0", borderBottom: "1px solid #f4f4f4", gap: 16 }}>
              <span style={{ color: "#888", flexShrink: 0 }}>{l}</span>
              <span style={{ fontWeight: 600, wordBreak: "break-all", textAlign: "right" }}>{v}</span>
            </div>
          ))}
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: 14, width: isMobile ? "100%" : 280, flexShrink: 0 }}>
          {!isMobile && (
            <Card>
              {avatar(56, 22)}
              <div style={{ fontWeight: 700, fontSize: 16, marginTop: 12, marginBottom: 2 }}>{user.name}</div>
              <div style={{ fontSize: 13, color: "#888", marginBottom: 12 }}>{user.email}</div>
              {user.company && <Badge color="blue">{user.company.name}</Badge>}
            </Card>
          )}
          {user.zones?.length > 0 && (
            <Card>
              <h4 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700 }}>Covered Zones</h4>
              {user.zones.map((z) => (
                <div key={z.id} style={{ fontSize: 13, color: "#555", padding: "4px 0", borderBottom: "1px solid #f4f4f4" }}>
                  {z.name} <span style={{ color: "#aaa" }}>({z.prefix})</span>
                </div>
              ))}
            </Card>
          )}
        </div>

      </div>
    </div>
  );
}

// ─── App Shell ────────────────────────────────────────────────────────────────
function AppLayout({ user, onLogout, api, showToast, toast, setToast }) {
  const isMobile = useIsMobile();

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f5f7fa", fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}>
      {isMobile && <TopBar user={user} onLogout={onLogout} />}
      <Sidebar user={user} onLogout={onLogout} />
      <main
        style={{
          flex: 1,
          padding: isMobile ? "20px 16px" : "32px 36px",
          paddingTop: isMobile ? 76 : "32px",
          paddingBottom: isMobile ? 84 : undefined,
          overflowY: "auto",
          maxHeight: "100vh",
          minWidth: 0,
        }}
      >
        <Outlet context={{ api, toast: showToast, user }} />
      </main>
      {isMobile && <BottomNav />}
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default function App() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => sessionStorage.getItem("pt_token") || "");
  const [user, setUser] = useState(() => { try { return JSON.parse(sessionStorage.getItem("pt_user") || "null"); } catch { return null; } });
  const [toast, setToast] = useState(null);

  const handleLogoutRef = useRef(null);
  const api = useApi(token, useCallback(() => handleLogoutRef.current?.(), []));

  const showToast = useCallback((msg, type = "success") => setToast({ msg, type }), []);

  const handleLogin = (t, u) => {
    setToken(t);
    setUser(u);
    sessionStorage.setItem("pt_token", t);
    sessionStorage.setItem("pt_user", JSON.stringify(u));
    navigate("/available", { replace: true });
  };

  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setToken("");
    setUser(null);
    sessionStorage.clear();
    navigate("/login", { replace: true });
  };
  handleLogoutRef.current = handleLogout;

  const authed = Boolean(token && user);

  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        * { box-sizing: border-box }
        html, body { margin: 0; padding: 0 }
      `}</style>
      <Routes>
        <Route
          path="/login"
          element={authed ? <Navigate to="/available" replace /> : <Login onLogin={handleLogin} />}
        />
        <Route
          element={
            authed ? (
              <AppLayout
                user={user}
                onLogout={handleLogout}
                api={api}
                showToast={showToast}
                toast={toast}
                setToast={setToast}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          <Route index element={<Navigate to="/available" replace />} />
          <Route path="available" element={<AvailableJobs />} />
          <Route path="myjobs" element={<MyJobs />} />
          <Route path="jobs/:jobId" element={<JobDetail />} />
          <Route path="addon-jobs" element={<AddonJobs />} />
          <Route path="addon-jobs/:jobId" element={<AddonJobDetail />} />
          <Route path="wallet" element={<Wallet />} />
          <Route path="withdrawals" element={<Withdrawals />} />
          <Route path="profile" element={<Profile />} />
        </Route>
        <Route path="*" element={<Navigate to={authed ? "/available" : "/login"} replace />} />
      </Routes>
    </>
  );
}
